import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import { useProductList, useProductFamilies, useFamilyAnalytics, useCompanyCodes, useManufacturerCodes, saveFamily, saveCompanyCode, deleteCompanyCode, saveManufacturerCode, deleteManufacturerCode, importProducts, assignSupplierBulk } from './hooks/useProductMeta';
import { userCanEditProducts, supplierKeyForUser } from './roles';
import { fieldVisible, generateProductName, NAME_INPUT_FIELDS, NAME_GENERATOR_TYPES, ASPECT_RATIO_OPTIONS, MOUNTING_OPTIONS } from './utils/productFields';

const API_BASE = process.env.REACT_APP_API_URL;

// Known manufacturing suppliers (canonical key → label). A supplier user only
// sees the products carrying their key.
const SUPPLIERS = [
  { key: 'gpv', label: 'GPV' },
  { key: 'ubiqconn', label: 'Ubiqconn' },
  { key: 'acmepoint', label: 'Acmepoint' },
  { key: 'hk_ringer', label: 'HK Ringer' },
];
const supplierLabel = (key) => (SUPPLIERS.find(s => s.key === key) || {}).label || key;

// Lifecycle status → label + colour.
const STATUS_META = {
  in_development: { label: 'In development', color: '#fbbf24' },
  released: { label: 'Released', color: '#60a5fa' },
  production: { label: 'Production', color: '#46bb89' },
  end_of_life: { label: 'End of life', color: '#f59e0b' },
  void: { label: 'Void', color: '#ef4444' },
};

const TYPE_META = {
  MON: { label: 'MON · Complete product', color: colors.accent },
  DIU: { label: 'DIU · Display sub-assembly', color: '#60a5fa' },
  ELU: { label: 'ELU · Electronics sub-assembly', color: '#fbbf24' },
  PAN: { label: 'PAN · Panel sub-assembly', color: '#a78bfa' },
  BOX: { label: 'BOX · Headless unit', color: '#34d399' },
  ACC: { label: 'ACC · Accessories', color: '#a78bfa' },
  COMPONENT: { label: 'COMPONENT · Parts (SSD/RAM)', color: '#f472b6' },
  SOFTWARE: { label: 'SOFTWARE · Licences & images', color: '#22d3ee' },
};
const TYPE_ORDER = ['MON', 'DIU', 'ELU', 'PAN', 'BOX', 'ACC', 'COMPONENT', 'SOFTWARE'];

export default function ProductsPage() {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  // Role handling mirrors the backend (any staff role wins; supplier scope only
  // when NOT staff; canonical supplier key) so the UI and API agree.
  const canEdit = userCanEditProducts(user);
  const mySupplierKey = supplierKeyForUser(user);
  const isSupplier = !!mySupplierKey;

  const [search, setSearch] = useState('');
  const [family, setFamily] = useState(''); // '' = all families
  const [company, setCompany] = useState(''); // '' = all companies (by customer code)
  const [type, setType] = useState('MON'); // default to monitors, not a wall of ACC
  const [qualityFilter, setQualityFilter] = useState('all');
  const [showInsights, setShowInsights] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [quickAddPn, setQuickAddPn] = useState(''); // no-result search term being added as a new P/N
  const { families, typeCounts, refetch: refetchFamilies } = useProductFamilies({ enabled: !!isSignedIn });
  // Cross-family analytics are NI-internal — suppliers don't fetch or see them.
  const { analytics } = useFamilyAnalytics({ enabled: !!isSignedIn && !isSupplier });
  const { codes, refetch: refetchCodes } = useCompanyCodes({ enabled: !!isSignedIn });
  const { letters, refetch: refetchLetters } = useManufacturerCodes({ enabled: !!isSignedIn });
  // When searching, drop the type filter so results span every type.
  const effectiveType = search.trim() ? '' : type;
  const { items, total, isLoading, error, refetch: refetchList } = useProductList({ search, family, company, type: effectiveType, limit: 1000, enabled: !!isSignedIn });
  const refetchAll = useCallback(() => { refetchFamilies(); refetchList(); }, [refetchFamilies, refetchList]);

  const selectedProfileEntry = useMemo(
    () => families.find(f => f.familyKey === family) || null,
    [families, family]
  );

  if (!isSignedIn) {
    return (
      <div className="ni-page"><div className="ni-shell ni-shell--narrow">
        <div className="ni-panel ni-empty">Please sign in to view product metadata.</div>
      </div></div>
    );
  }

  const passesQuality = (p) => {
    if (qualityFilter === 'all') return true;
    const q = p.quality;
    if (!q) return qualityFilter === 'below_b' || qualityFilter === 'issues';
    if (qualityFilter === 'A') return q.grade === 'A';
    if (qualityFilter === 'B_plus') return q.overall >= 70;
    if (qualityFilter === 'below_b') return q.overall < 70;
    if (qualityFilter === 'issues') return (q.issues || []).length > 0;
    return true;
  };
  const visibleItems = items.filter(passesQuality);
  const grouped = TYPE_ORDER
    .map(t => ({ type: t, list: visibleItems.filter(p => (p.type || 'MON') === t) }))
    .filter(g => g.list.length > 0);
  const totalCount = Object.values(typeCounts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="ni-page">
      <div className="ni-shell ni-shell--narrow">
        <header className="ni-header">
          <div className="ni-brand">
            <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            </Link>
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Product Metadata</span>
              <span className="ni-brand-subtitle">Family-level metadata + product numbers</span>
            </div>
          </div>
          <Link to="/product-sheet" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            📄 Sales sheet
          </Link>
        </header>

        {isSupplier && (
          <div className="ni-panel" style={{ marginBottom: '1rem', borderLeft: `4px solid ${colors.accent}`, fontSize: '0.85rem', color: colors.textBody }}>
            Showing the products assigned to <strong style={{ color: colors.textStrong }}>{supplierLabel(mySupplierKey)}</strong>.
          </div>
        )}

        {/* Toolbar: search front-and-centre, filters and menus on one row */}
        <div className="ni-panel" style={{ padding: '0.85rem', marginBottom: '1rem', position: 'sticky', top: 8, zIndex: 5 }}>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setQuickAddPn(''); }}
              placeholder="Search product number, name or family…"
              className="ni-input"
              style={{ flex: 1, minWidth: 220 }}
            />
            <select value={family} onChange={e => { setFamily(e.target.value); if (e.target.value) setType(''); }} className="ni-select" style={{ minWidth: 170 }} title="Family">
              <option value="">All families</option>
              {families.map(f => (
                <option key={f.familyKey} value={f.familyKey}>
                  {(f.profile?.displayName || f.familyKey)} ({f.productCount})
                </option>
              ))}
            </select>
            <select
              value={company}
              onChange={e => { setCompany(e.target.value); if (e.target.value) setType(''); }}
              className="ni-select" style={{ minWidth: 170 }} title="Customer / company"
            >
              <option value="">All companies</option>
              {[...codes].filter(c => c.company).sort((a, b) => a.company.localeCompare(b.company)).map(c => (
                <option key={c.code} value={c.code}>{c.company} ({c.code})</option>
              ))}
            </select>
            <select value={qualityFilter} onChange={e => setQualityFilter(e.target.value)} className="ni-select" style={{ minWidth: 150 }} title="Filter by data quality">
              <option value="all">All quality</option>
              <option value="A">Grade A</option>
              <option value="B_plus">Grade A–B</option>
              <option value="below_b">Below B</option>
              <option value="issues">Has issues</option>
            </select>
            {!isSupplier && (
              <button type="button" className="ni-button ni-button--ghost" style={toolbarBtn(showInsights)} onClick={() => setShowInsights(v => !v)}>
                📊 Insights {showInsights ? '▾' : '▸'}
              </button>
            )}
            {canEdit && (
              <button type="button" className="ni-button ni-button--ghost" style={toolbarBtn(showTools)} onClick={() => setShowTools(v => !v)}>
                ⚙ Tools {showTools ? '▾' : '▸'}
              </button>
            )}
          </div>

          {/* Type tabs — disabled while searching (search spans all types) */}
          {!search.trim() && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.65rem' }}>
              <TypeTab label="All" count={totalCount} active={type === ''} onClick={() => setType('')} color={colors.textBody} />
              {TYPE_ORDER.filter(t => (typeCounts || {})[t]).map(t => (
                <TypeTab key={t} label={t} count={typeCounts[t]} active={type === t} onClick={() => setType(t)} color={TYPE_META[t]?.color} />
              ))}
            </div>
          )}
        </div>

        {/* Insights drawer (collapsed by default) */}
        {showInsights && (
          <>
            <FamilyAnalyticsPanel analytics={analytics} filterKey={family} />
            <FamilyQualityPanel families={families} filterKey={family} />
          </>
        )}

        {/* Admin tools drawer (collapsed by default) */}
        {canEdit && showTools && (
          <>
            <ImportProductsPanel getToken={getToken} onImported={refetchAll} />
            <SupplierAccessPanel getToken={getToken} families={families} onChanged={refetchAll} />
            <AddProductPanel getToken={getToken} families={families} onAdded={refetchAll} />
            <CompanyCodesPanel codes={codes} canEdit={canEdit} getToken={getToken} onChanged={refetchCodes} />
            <ManufacturerCodesPanel letters={letters} canEdit={canEdit} getToken={getToken} onChanged={refetchLetters} />
          </>
        )}

        {/* Family-level metadata for the selected family */}
        {family && (
          <FamilyProfilePanel
            familyKey={family}
            entry={selectedProfileEntry}
            canEdit={canEdit}
            getToken={getToken}
            onSaved={refetchFamilies}
          />
        )}

        {!isLoading && !error && (
          <div style={{ fontSize: '0.78rem', color: colors.textMuted, margin: '0 0 0.6rem' }}>
            {visibleItems.length}{total > items.length ? ` of ${total}` : ''} {visibleItems.length === 1 ? 'product' : 'products'}
            {qualityFilter !== 'all' ? ' · quality filter active' : ''}
          </div>
        )}

        {isLoading ? (
          <div className="ni-panel" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <LoadingSpinner size={48} />
          </div>
        ) : error ? (
          <div className="ni-panel ni-empty">Failed to load products. {error.message}</div>
        ) : grouped.length === 0 ? (
          quickAddPn ? (
            <AddProductPanel key={quickAddPn} getToken={getToken} families={families} onAdded={refetchAll} initialPn={quickAddPn} startOpen />
          ) : (
            <div className="ni-panel ni-empty">
              {isSupplier ? (
                'No products are assigned to your organisation yet.'
              ) : search.trim() ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.7rem' }}>
                  <span>No products match “{search.trim()}”.</span>
                  {canEdit && (
                    <button type="button" className="ni-button ni-button--primary" style={{ fontSize: '0.85rem' }}
                      onClick={() => setQuickAddPn(search.trim())}>
                      + Add “{search.trim()}” as a new part number
                    </button>
                  )}
                </div>
              ) : (
                'No products found.'
              )}
            </div>
          )
        ) : (
          grouped.map(group => (
            <section key={group.type} style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '0.95rem', color: TYPE_META[group.type]?.color || colors.textStrong, margin: '0 0 0.6rem' }}>
                {TYPE_META[group.type]?.label || group.type} <span style={{ color: colors.textMuted, fontWeight: 400 }}>({group.list.length})</span>
              </h2>
              <div className="ni-grid ni-grid--three">
                {group.list.map(p => (
                  <Link key={p.productNumber} to={`/product/${encodeURIComponent(p.productNumber)}`} style={{ textDecoration: 'none' }}>
                    <div style={cardStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
                        <span style={{ fontWeight: 700, color: colors.textStrong, fontFamily: "'Barlow Condensed','Barlow',sans-serif", letterSpacing: '0.03em' }}>
                          {p.productNumber}
                        </span>
                        {p.status && STATUS_META[p.status] && (
                          <span style={statusBadgeStyle(STATUS_META[p.status].color)}>{STATUS_META[p.status].label}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: colors.textBody }}>{p.name || '—'}</div>
                      <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                        {[p.family, p.size].filter(Boolean).join(' · ') || '—'}
                      </div>
                      {p.company && (
                        <div style={{ marginTop: '0.2rem' }}>
                          <span style={companyTagStyle(p.resolvedCompanyCode)}>
                            {p.company}
                            {p.companyWarrantyExtraMonths > 0 ? ` · +${p.companyWarrantyExtraMonths} mo` : ''}
                          </span>
                        </div>
                      )}
                      {(p.linkedDiu || p.linkedElu || p.parentMon) && (
                        <div style={{ fontSize: '0.72rem', color: colors.textDim, marginTop: '0.25rem' }}>
                          {p.type === 'MON'
                            ? `DIU ${p.linkedDiu || '—'} · ELU ${p.linkedElu || '—'}`
                            : `MON ${p.parentMon || '—'}`}
                        </div>
                      )}
                      {p.quality && <QualityBar quality={p.quality} />}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function FamilyProfilePanel({ familyKey, entry, canEdit, getToken, onSaved }) {
  const profile = entry?.profile || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const startEdit = () => {
    setForm({
      displayName: profile.displayName || '',
      accentColor: profile.accentColor || '',
      description: profile.description || '',
      notes: profile.notes || '',
      warranty: {
        months: profile.warranty?.months ?? '',
        startBasis: profile.warranty?.startBasis || 'scandate',
        terms: profile.warranty?.terms || '',
      },
      specs: Array.isArray(profile.specs) ? profile.specs.map(s => ({ ...s })) : [],
    });
    setErr('');
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const payload = {
        displayName: form.displayName,
        accentColor: form.accentColor,
        description: form.description,
        notes: form.notes,
        warranty: {
          months: form.warranty.months === '' ? null : Number(form.warranty.months),
          startBasis: form.warranty.startBasis,
          terms: form.warranty.terms,
        },
        specs: form.specs.filter(s => s.key || s.value),
      };
      await saveFamily(getToken, familyKey, payload);
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const accent = profile.accentColor || colors.accent;

  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem', borderLeft: `4px solid ${accent}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.6rem' }}>
        <div>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Family</span>
          <h2 style={{ margin: '0.1rem 0 0', color: colors.textStrong }}>{profile.displayName || familyKey}</h2>
          <span style={{ fontSize: '0.78rem', color: colors.textMuted }}>{familyKey} · {entry?.productCount ?? 0} product number(s)</span>
        </div>
        {canEdit && !editing && (
          <button className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }} onClick={startEdit}>Edit family</button>
        )}
      </div>

      {!editing ? (
        <div style={gridStyle}>
          <Field label="Warranty" value={profile.warranty?.months != null ? `${profile.warranty.months} months (from ${profile.warranty.startBasis || 'scandate'})` : '—'} />
          <Field label="Warranty terms" value={profile.warranty?.terms} />
          <Field label="Description" value={profile.description} />
          <Field label="Notes" value={profile.notes} />
          {(profile.specs || []).map((s, i) => <Field key={i} label={s.label || s.key} value={s.value} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <div style={gridStyle}>
            <Input label="Display name" value={form.displayName} onChange={v => setForm({ ...form, displayName: v })} />
            <Input label="Accent colour (hex)" value={form.accentColor} onChange={v => setForm({ ...form, accentColor: v })} />
            <Input label="Warranty months" type="number" value={form.warranty.months} onChange={v => setForm({ ...form, warranty: { ...form.warranty, months: v } })} />
            <label style={fieldWrap}><span style={labelStyle}>Warranty starts from</span>
              <select value={form.warranty.startBasis} onChange={e => setForm({ ...form, warranty: { ...form.warranty, startBasis: e.target.value } })} style={inputStyle}>
                <option value="scandate">Scan date</option>
                <option value="ship">Ship date</option>
                <option value="claim">Claim date</option>
              </select>
            </label>
            <Input label="Warranty terms" value={form.warranty.terms} onChange={v => setForm({ ...form, warranty: { ...form.warranty, terms: v } })} />
            <Input label="Description" value={form.description} onChange={v => setForm({ ...form, description: v })} />
          </div>
          <label style={fieldWrap}><span style={labelStyle}>Notes</span>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </label>
          <div>
            <span style={labelStyle}>Shared family specs</span>
            {form.specs.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                <input value={s.label || s.key || ''} onChange={e => setForm({ ...form, specs: form.specs.map((x, idx) => idx === i ? { ...x, label: e.target.value, key: e.target.value } : x) })} placeholder="Field" style={{ ...inputStyle, flex: 1 }} />
                <input value={s.value || ''} onChange={e => setForm({ ...form, specs: form.specs.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x) })} placeholder="Value" style={{ ...inputStyle, flex: 2 }} />
                <button type="button" className="ni-button ni-button--ghost" style={{ padding: '0 0.6rem' }} onClick={() => setForm({ ...form, specs: form.specs.filter((_, idx) => idx !== i) })}>×</button>
              </div>
            ))}
            <button type="button" className="ni-button ni-button--ghost" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }} onClick={() => setForm({ ...form, specs: [...form.specs, { key: '', label: '', value: '' }] })}>+ Add spec</button>
          </div>
          {err && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: 0 }}>{err}</p>}
          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
            <button className="ni-button ni-button--ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            <button className="ni-button ni-button--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save family'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TypeTab({ label, count, active, onClick, color }) {
  return (
    <button type="button" onClick={onClick} style={{
      fontSize: '0.78rem', fontWeight: 700, padding: '0.25rem 0.6rem', borderRadius: '999px', cursor: 'pointer',
      border: `1px solid ${active ? (color || colors.accent) : colors.panelBorder}`,
      background: active ? `${color || colors.accent}22` : 'transparent',
      color: active ? (color || colors.accent) : colors.textBody,
    }}>
      {label} <span style={{ opacity: 0.7, fontWeight: 500 }}>{count}</span>
    </button>
  );
}

export function qualityColor(n) {
  if (n >= 80) return '#46bb89';
  if (n >= 50) return '#fbbf24';
  return '#ef4444';
}

// Compact two-score quality footer on a product card.
function QualityBar({ quality }) {
  const { completeness, correctness, grade, issues } = quality;
  const title = (issues && issues.length) ? `Issues:\n• ${issues.join('\n• ')}` : 'No issues detected';
  const cell = (label, val) => (
    <div style={{ flex: 1 }} title={title}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: colors.textMuted }}>
        <span>{label}</span><span style={{ color: qualityColor(val), fontWeight: 700 }}>{val}</span>
      </div>
      <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', marginTop: 1 }}>
        <div style={{ width: `${val}%`, height: '100%', borderRadius: 2, background: qualityColor(val) }} />
      </div>
    </div>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.45rem', paddingTop: '0.4rem', borderTop: `1px solid ${colors.panelBorder}` }} title={title}>
      <span style={{ fontSize: '0.7rem', fontWeight: 800, color: qualityColor(quality.overall), border: `1px solid ${qualityColor(quality.overall)}55`, borderRadius: 5, padding: '0 0.3rem' }}>{grade}</span>
      {cell('Complete', completeness)}
      {cell('Valid', correctness)}
    </div>
  );
}

// Blank product in the canonical import schema — used for the downloadable
// template and as the starting point for the in-app configurator.
const PRODUCT_TEMPLATE = {
  productNumber: '',
  type: 'MON',
  family: 'wave_ii',
  name: '',
  size: '',
  status: 'in_development',
  companyCode: '',
  touch: null,
  nvis: null,
  powerSupply: '',
  bonding: '',
  brightness: '',
  interfaces: '',
  aspectRatio: '',
  resolution: '',
  ipRating: '',
  operatingVoltage: '',
  certifications: [],
  mounting: [],
  linkedDiu: '',
  linkedElu: '',
  parentMon: '',
  bomId: '',
  bomDocumentNumber: '',
  bomName: '',
  customerModelName: '',
  customerInternalPN: '',
  datasheetConfirmed: false,
  serialNumberLayout: '',
  transmittalRef: '',
  biosReady: null,
  ninApproval: '',
  specs: [{ key: '', value: '' }],
  notes: '',
};

function downloadTemplate() {
  const blob = new Blob([JSON.stringify(PRODUCT_TEMPLATE, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'product-template.json';
  a.click();
  URL.revokeObjectURL(url);
}

function ImportProductsPanel({ getToken, onImported }) {
  const [open, setOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter(f => f.name.toLowerCase().endsWith('.json'));
    if (!files.length) { setErr('Drop one or more .json product files.'); return; }
    setBusy(true); setErr(''); setResult(null);
    try {
      const products = [];
      for (const f of files) {
        const text = await f.text();
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) products.push(...parsed);
        else if (Array.isArray(parsed.products)) products.push(...parsed.products);
        else products.push(parsed);
      }
      const res = await importProducts(getToken, products);
      setResult(res);
      onImported && onImported();
    } catch (e) {
      setErr(e.message || 'Import failed — is the JSON valid?');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Import products — drag &amp; drop JSON
        </div>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '0.75rem' }}>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            style={{
              border: `2px dashed ${dragOver ? colors.accent : colors.panelBorder}`,
              borderRadius: '12px', padding: '1.5rem', textAlign: 'center',
              background: dragOver ? 'rgba(70,187,137,0.06)' : 'transparent', transition: 'all 0.15s',
            }}
          >
            <p style={{ margin: '0 0 0.5rem', color: colors.textBody, fontSize: '0.9rem' }}>
              {busy ? 'Importing…' : 'Drop product .json file(s) here'}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <label className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', cursor: 'pointer' }}>
                Choose files
                <input type="file" accept=".json,application/json" multiple style={{ display: 'none' }}
                  onChange={e => e.target.files.length && handleFiles(e.target.files)} disabled={busy} />
              </label>
              <button type="button" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }}
                onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}>⬇ Download template</button>
            </div>
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.74rem', color: colors.textMuted }}>
              One product per file (the exported schema), or an array. Upsert is keyed by product number.
              No JSON? Use the configurator below.
            </p>
          </div>
          {result && (
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.85rem', color: colors.accent }}>
              Imported {result.total} — {result.created} created, {result.updated} updated
              {result.errors?.length ? `, ${result.errors.length} skipped` : ''}.
            </p>
          )}
          {err && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: '0.6rem 0 0' }}>{err}</p>}
        </div>
      )}
    </div>
  );
}

// In-app configurator — add a product through a form instead of writing JSON.
// Also mounted by the no-result search helper, prefilled with the searched P/N.
function AddProductPanel({ getToken, families, onAdded, initialPn = '', startOpen = false }) {
  const [open, setOpen] = useState(!!startOpen);
  const [form, setForm] = useState(() => ({ ...PRODUCT_TEMPLATE, productNumber: initialPn, specs: [{ key: '', value: '' }] }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savedPn, setSavedPn] = useState('');
  // New products auto-name from the structured variables; typing a name turns it off.
  const [nameAuto, setNameAuto] = useState(true);
  const [certsText, setCertsText] = useState('');
  const [boms, setBoms] = useState([]);

  const set = (k, v) => {
    if (k === 'name') setNameAuto(false);
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k !== 'name' && nameAuto && NAME_INPUT_FIELDS.includes(k)) {
        const gen = generateProductName(next);
        if (gen) next.name = gen;
      }
      return next;
    });
  };

  // Released BOMs for the link picker, fetched once the panel opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/quality/boms`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!cancelled) setBoms((data.boms || []).filter(b => b.currentStatus === 'Released'));
      } catch {
        if (!cancelled) setBoms([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, getToken]);

  const toggleMounting = (opt) => setForm(f => {
    const cur = Array.isArray(f.mounting) ? f.mounting : [];
    return { ...f, mounting: cur.includes(opt) ? cur.filter(m => m !== opt) : [...cur, opt] };
  });

  const reset = () => { setForm({ ...PRODUCT_TEMPLATE, specs: [{ key: '', value: '' }] }); setErr(''); setSavedPn(''); setNameAuto(true); setCertsText(''); };

  const submit = async () => {
    const pn = (form.productNumber || '').trim();
    if (!pn) { setErr('Product number is required.'); return; }
    setBusy(true); setErr(''); setSavedPn('');
    try {
      // The import upserts by product number — creating over an existing record
      // would blank its fields. Existing products are edited on their own page.
      const token = await getToken();
      const check = await fetch(`${API_BASE}/product-meta/${encodeURIComponent(pn)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (check.ok) {
        setErr(`${pn} already exists — open its product page to edit it instead.`);
        setBusy(false);
        return;
      }
      const payload = {
        ...form,
        productNumber: pn,
        specs: (form.specs || []).filter(s => s.key || s.value),
        certifications: certsText.split(',').map(s => s.trim()).filter(Boolean),
      };
      await importProducts(getToken, [payload]);
      setSavedPn(pn);
      setForm({ ...PRODUCT_TEMPLATE, specs: [{ key: '', value: '' }] });
      setNameAuto(true); setCertsText('');
      onAdded && onAdded();
    } catch (e) {
      setErr(e.message || 'Could not add product.');
    } finally { setBusy(false); }
  };

  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Add a product — configurator
        </div>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <div style={gridStyle}>
            <label style={fieldWrap}><span style={labelStyle}>Product number *</span>
              <input value={form.productNumber} onChange={e => set('productNumber', e.target.value)} placeholder="WE270 000 800" style={inputStyle} />
            </label>
            <label style={fieldWrap}><span style={labelStyle}>Type</span>
              <select value={form.type} onChange={e => set('type', e.target.value)} style={inputStyle}>
                {['MON', 'DIU', 'ELU', 'PAN', 'ACC', 'BOX', 'COMPONENT', 'SOFTWARE'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label style={fieldWrap}><span style={labelStyle}>Family</span>
              <input list="ni-family-options" value={form.family} onChange={e => set('family', e.target.value)} placeholder="wave_ii" style={inputStyle} />
              <datalist id="ni-family-options">
                {(families || []).map(f => <option key={f.familyKey} value={f.familyKey} />)}
              </datalist>
            </label>
            <label style={fieldWrap}><span style={labelStyle}>Lifecycle status</span>
              <select value={form.status} onChange={e => set('status', e.target.value)} style={inputStyle}>
                {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
              </select>
            </label>
            <div style={fieldWrap}>
              <span style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.4rem' }}>
                <span>Name{nameAuto && NAME_GENERATOR_TYPES.includes(form.type) ? ' · auto' : ''}</span>
                {!nameAuto && NAME_GENERATOR_TYPES.includes(form.type) && (
                  <button type="button" className="ni-button ni-button--ghost" style={{ fontSize: '0.66rem', padding: '0 0.4rem', lineHeight: 1.6 }}
                    onClick={() => { const gen = generateProductName(form); if (gen) { setNameAuto(true); setForm(f => ({ ...f, name: gen })); } }}
                    title="Resume generating the name from the structured variables">
                    ⟳ Auto
                  </button>
                )}
              </span>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                placeholder={NAME_GENERATOR_TYPES.includes(form.type) ? 'builds itself from the variables below' : ''} style={inputStyle} />
            </div>
            {fieldVisible('size', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Size</span>
                <input value={form.size} onChange={e => set('size', e.target.value)} placeholder='27"' style={inputStyle} />
              </label>
            )}
            {fieldVisible('touch', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Touch</span>
                <select value={String(form.touch)} onChange={e => set('touch', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} style={inputStyle}>
                  <option value="null">—</option><option value="true">Touch</option><option value="false">Non-touch</option>
                </select>
              </label>
            )}
            {fieldVisible('nvis', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>NVIS</span>
                <select value={String(form.nvis)} onChange={e => set('nvis', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} style={inputStyle}>
                  <option value="null">—</option><option value="true">NVIS</option><option value="false">Non-NVIS</option>
                </select>
              </label>
            )}
            {fieldVisible('brightness', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Brightness</span>
                <select value={form.brightness} onChange={e => set('brightness', e.target.value)} style={inputStyle}>
                  <option value="">—</option><option value="Standard">Standard</option><option value="High bright">High bright</option>
                </select>
              </label>
            )}
            {fieldVisible('aspectRatio', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Aspect ratio</span>
                <select value={form.aspectRatio} onChange={e => set('aspectRatio', e.target.value)} style={inputStyle}>
                  <option value="">—</option>
                  {ASPECT_RATIO_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            )}
            {fieldVisible('resolution', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Resolution</span>
                <input value={form.resolution} onChange={e => set('resolution', e.target.value)} placeholder="1920x1080" style={inputStyle} />
              </label>
            )}
            {fieldVisible('ipRating', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>IP rating</span>
                <input value={form.ipRating} onChange={e => set('ipRating', e.target.value)} placeholder="IP56" style={inputStyle} />
              </label>
            )}
            {fieldVisible('bonding', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Bonding</span>
                <select value={form.bonding} onChange={e => set('bonding', e.target.value)} style={inputStyle}>
                  <option value="">—</option><option value="Air">Air</option><option value="Optical">Optical</option>
                </select>
              </label>
            )}
            {fieldVisible('powerSupply', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Power supply</span>
                <select value={form.powerSupply} onChange={e => set('powerSupply', e.target.value)} style={inputStyle}>
                  <option value="">—</option>
                  {['AC', 'DC', 'AC/DC', 'AC/AC', 'DC/DC'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            )}
            {fieldVisible('operatingVoltage', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Operating voltage</span>
                <input value={form.operatingVoltage} onChange={e => set('operatingVoltage', e.target.value)} placeholder="115/230 VAC, 24 VDC" style={inputStyle} />
              </label>
            )}
            {fieldVisible('interfaces', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Interfaces</span>
                <input value={form.interfaces} onChange={e => set('interfaces', e.target.value)} placeholder="RS422/485, RS232" style={inputStyle} />
              </label>
            )}
            <label style={fieldWrap}><span style={labelStyle}>Company code override</span>
              <input value={form.companyCode} onChange={e => set('companyCode', e.target.value)} placeholder="auto from P/N" style={inputStyle} />
            </label>
            <label style={fieldWrap}><span style={labelStyle}>Customer model name</span>
              <input value={form.customerModelName} onChange={e => set('customerModelName', e.target.value)} placeholder="NVD18T-HB" style={inputStyle} />
            </label>
            {fieldVisible('certifications', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Certifications / approvals</span>
                <input value={certsText} onChange={e => setCertsText(e.target.value)} placeholder="IEC 60945, DNV, MIL-STD-810" style={inputStyle} />
              </label>
            )}
            {fieldVisible('bomLink', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Linked BOM — drives revision</span>
                <select value={form.bomId} onChange={e => {
                  const b = boms.find(x => x._id === e.target.value);
                  setForm(f => ({ ...f, bomId: b ? b._id : '', bomDocumentNumber: b ? b.documentNumber : '', bomName: b ? b.name : '' }));
                }} style={inputStyle}>
                  <option value="">— none —</option>
                  {boms.map(b => <option key={b._id} value={b._id}>{b.documentNumber} — {b.name} (rev {b.currentRevision})</option>)}
                </select>
              </label>
            )}
            {fieldVisible('linkedDiu', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Linked DIU P/N</span>
                <input value={form.linkedDiu} onChange={e => set('linkedDiu', e.target.value)} style={inputStyle} />
              </label>
            )}
            {fieldVisible('linkedElu', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Linked ELU P/N</span>
                <input value={form.linkedElu} onChange={e => set('linkedElu', e.target.value)} style={inputStyle} />
              </label>
            )}
            {fieldVisible('parentMon', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Parent MON P/N</span>
                <input value={form.parentMon} onChange={e => set('parentMon', e.target.value)} style={inputStyle} />
              </label>
            )}
            {fieldVisible('datasheetConfirmed', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Datasheet confirmed</span>
                <select value={String(!!form.datasheetConfirmed)} onChange={e => set('datasheetConfirmed', e.target.value === 'true')} style={inputStyle}>
                  <option value="false">No</option><option value="true">Yes</option>
                </select>
              </label>
            )}
            {fieldVisible('serialNumberLayout', form.type) && (
              <label style={fieldWrap}><span style={labelStyle}>Serial number layout</span>
                <input value={form.serialNumberLayout} onChange={e => set('serialNumberLayout', e.target.value)} style={inputStyle} />
              </label>
            )}
          </div>

          {fieldVisible('mounting', form.type) && (
            <div>
              <span style={labelStyle}>Mounting options</span>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                {MOUNTING_OPTIONS.map(opt => {
                  const on = (form.mounting || []).includes(opt);
                  return (
                    <button type="button" key={opt} onClick={() => toggleMounting(opt)} style={chipToggle(on)}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <label style={fieldWrap}><span style={labelStyle}>Notes</span>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </label>

          <div>
            <span style={labelStyle}>Specifications</span>
            {form.specs.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                <input value={s.key} onChange={e => set('specs', form.specs.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))} placeholder="Field" style={{ ...inputStyle, flex: 1 }} />
                <input value={s.value} onChange={e => set('specs', form.specs.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))} placeholder="Value" style={{ ...inputStyle, flex: 2 }} />
                <button type="button" className="ni-button ni-button--ghost" style={{ padding: '0 0.6rem' }} onClick={() => set('specs', form.specs.filter((_, idx) => idx !== i))}>×</button>
              </div>
            ))}
            <button type="button" className="ni-button ni-button--ghost" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }} onClick={() => set('specs', [...form.specs, { key: '', value: '' }])}>+ Add spec</button>
          </div>

          {err && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: 0 }}>{err}</p>}
          {savedPn && (
            <p style={{ color: colors.accent, fontSize: '0.85rem', margin: 0 }}>
              Added <Link to={`/product/${encodeURIComponent(savedPn)}`} style={{ color: colors.accent, fontWeight: 700 }}>{savedPn}</Link> — fill in the rest on its page.
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
            <button type="button" className="ni-button ni-button--ghost" onClick={reset} disabled={busy}>Reset</button>
            <button type="button" className="ni-button ni-button--primary" onClick={submit} disabled={busy}>{busy ? 'Adding…' : 'Add product'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Assign (or remove) a manufacturing supplier across many products at once.
// With no type/family filter it covers the whole catalogue — this is how NI seeds
// e.g. GPV onto every product as a starting point.
function SupplierAccessPanel({ getToken, families, onChanged }) {
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState(SUPPLIERS[0].key);
  const [type, setType] = useState('');
  const [family, setFamily] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async (action) => {
    const scopeLabel = [type, family].filter(Boolean).join(' / ') || 'ALL products';
    const verb = action === 'remove' ? 'Remove' : 'Assign';
    if (!window.confirm(`${verb} ${supplierLabel(supplier)} ${action === 'remove' ? 'from' : 'to'} ${scopeLabel}?`)) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const data = await assignSupplierBulk(getToken, { supplier, action, type: type || undefined, family: family || undefined });
      setResult(data);
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message || 'Failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Supplier access — assign a manufacturer to products
        </div>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '0.75rem' }}>
          <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: colors.textMuted }}>
            A supplier user only sees products carrying their key. Leave the filters empty to apply to the whole catalogue —
            e.g. assign <strong>GPV</strong> to all products to start.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={fieldWrap}><span style={labelStyle}>Supplier</span>
              <select value={supplier} onChange={e => setSupplier(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
                {SUPPLIERS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label style={fieldWrap}><span style={labelStyle}>Type (optional)</span>
              <select value={type} onChange={e => setType(e.target.value)} style={{ ...inputStyle, minWidth: 130 }}>
                <option value="">All types</option>
                {TYPE_ORDER.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label style={fieldWrap}><span style={labelStyle}>Family (optional)</span>
              <select value={family} onChange={e => setFamily(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
                <option value="">All families</option>
                {(families || []).map(f => <option key={f.familyKey} value={f.familyKey}>{f.profile?.displayName || f.familyKey}</option>)}
              </select>
            </label>
            <button type="button" className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }} disabled={busy} onClick={() => run('add')}>
              {busy ? 'Working…' : 'Assign'}
            </button>
            <button type="button" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem' }} disabled={busy} onClick={() => run('remove')}>
              Remove
            </button>
          </div>
          {result && (
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.85rem', color: colors.accent }}>
              {result.action === 'remove' ? 'Removed' : 'Assigned'} {supplierLabel(result.supplier)} — {result.modified} of {result.matched} product(s) changed.
            </p>
          )}
          {err && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: '0.6rem 0 0' }}>{err}</p>}
        </div>
      )}
    </div>
  );
}

function CompanyCodesPanel({ codes, canEdit, getToken, onChanged }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ code: '', company: '', warrantyExtraMonths: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const persist = async (code, payload) => {
    setBusy(true); setErr('');
    try {
      await saveCompanyCode(getToken, code, payload);
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally { setBusy(false); }
  };

  const addCode = async () => {
    if (!/^\d{1,2}$/.test(draft.code.trim())) { setErr('Code must be 1–2 digits.'); return; }
    await persist(draft.code.trim(), {
      company: draft.company.trim(),
      warrantyExtraMonths: Number(draft.warrantyExtraMonths) || 0,
    });
    setDraft({ code: '', company: '', warrantyExtraMonths: '' });
  };

  const removeCode = async (code) => {
    setBusy(true); setErr('');
    try {
      await deleteCompanyCode(getToken, code);
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message || 'Delete failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Company codes — part-number middle digits ({codes.length})
        </div>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '0.75rem' }}>
          <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: colors.textMuted }}>
            The middle group of a part number encodes the customer — e.g. <code>WE213 007 600</code> → code <strong>07</strong>.
            Code <strong>00</strong> is North Invent. Warranty months here are added on top of the family base.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: colors.textMuted }}>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Company</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>+ Warranty (months)</th>
                {canEdit && <th style={{ ...thStyle, width: 40 }} />}
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <CompanyCodeRow key={c.code} row={c} canEdit={canEdit} busy={busy} onSave={persist} onDelete={removeCode} />
              ))}
            </tbody>
          </table>

          {canEdit && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={fieldWrap}><span style={labelStyle}>Code</span>
                <input value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value })} placeholder="07" style={{ ...inputStyle, width: 80 }} />
              </label>
              <label style={{ ...fieldWrap, flex: 1, minWidth: 160 }}><span style={labelStyle}>Company</span>
                <input value={draft.company} onChange={e => setDraft({ ...draft, company: e.target.value })} placeholder="SAAB" style={inputStyle} />
              </label>
              <label style={fieldWrap}><span style={labelStyle}>+ Months</span>
                <input type="number" value={draft.warrantyExtraMonths} onChange={e => setDraft({ ...draft, warrantyExtraMonths: e.target.value })} placeholder="12" style={{ ...inputStyle, width: 100 }} />
              </label>
              <button className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }} disabled={busy} onClick={addCode}>Add code</button>
            </div>
          )}
          {err && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: '0.5rem 0 0' }}>{err}</p>}
        </div>
      )}
    </div>
  );
}

function CompanyCodeRow({ row, canEdit, busy, onSave, onDelete }) {
  const [company, setCompany] = useState(row.company || '');
  const [months, setMonths] = useState(row.warrantyExtraMonths ?? 0);
  const dirty = company !== (row.company || '') || Number(months) !== Number(row.warrantyExtraMonths ?? 0);
  return (
    <tr style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
      <td style={{ ...tdStyle, fontWeight: 700, color: colors.textStrong }}>{row.code}</td>
      <td style={tdStyle}>
        {canEdit
          ? <input value={company} onChange={e => setCompany(e.target.value)} style={{ ...inputStyle, padding: '0.3rem 0.5rem' }} />
          : (row.company || '—')}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {canEdit
          ? <input type="number" value={months} onChange={e => setMonths(e.target.value)} style={{ ...inputStyle, padding: '0.3rem 0.5rem', width: 90, textAlign: 'right' }} />
          : (row.warrantyExtraMonths || 0)}
      </td>
      {canEdit && (
        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {dirty && (
            <button className="ni-button ni-button--primary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginRight: 4 }} disabled={busy}
              onClick={() => onSave(row.code, { company, warrantyExtraMonths: Number(months) || 0 })}>Save</button>
          )}
          {row.code !== '00' && (
            <button className="ni-button ni-button--ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} disabled={busy}
              onClick={() => onDelete(row.code)}>×</button>
          )}
        </td>
      )}
    </tr>
  );
}

function ManufacturerCodesPanel({ letters, canEdit, getToken, onChanged }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ letter: '', manufacturer: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const persist = async (letter, payload) => {
    setBusy(true); setErr('');
    try {
      await saveManufacturerCode(getToken, letter, payload);
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally { setBusy(false); }
  };

  const addLetter = async () => {
    if (!/^[A-Za-z]$/.test(draft.letter.trim())) { setErr('Letter must be a single A–Z.'); return; }
    await persist(draft.letter.trim().toUpperCase(), { manufacturer: draft.manufacturer.trim() });
    setDraft({ letter: '', manufacturer: '' });
  };

  const removeLetter = async (letter) => {
    setBusy(true); setErr('');
    try {
      await deleteManufacturerCode(getToken, letter);
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message || 'Delete failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Manufacturer codes — service-tag prefix letter ({letters.length})
        </div>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>{open ? '▾' : '▸'}</span>
      </div>

      {open && (
        <div style={{ marginTop: '0.75rem' }}>
          <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: colors.textMuted }}>
            The first letter of a service tag is the factory — e.g. <code>C215123456</code> → <strong>C</strong> (GPV).
            Shown on each device page.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: colors.textMuted }}>
                <th style={thStyle}>Letter</th>
                <th style={thStyle}>Manufacturer</th>
                {canEdit && <th style={{ ...thStyle, width: 40 }} />}
              </tr>
            </thead>
            <tbody>
              {letters.map(l => (
                <ManufacturerCodeRow key={l.letter} row={l} canEdit={canEdit} busy={busy} onSave={persist} onDelete={removeLetter} />
              ))}
            </tbody>
          </table>

          {canEdit && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={fieldWrap}><span style={labelStyle}>Letter</span>
                <input value={draft.letter} maxLength={1} onChange={e => setDraft({ ...draft, letter: e.target.value })} placeholder="E" style={{ ...inputStyle, width: 70 }} />
              </label>
              <label style={{ ...fieldWrap, flex: 1, minWidth: 160 }}><span style={labelStyle}>Manufacturer</span>
                <input value={draft.manufacturer} onChange={e => setDraft({ ...draft, manufacturer: e.target.value })} placeholder="Factory name" style={inputStyle} />
              </label>
              <button className="ni-button ni-button--primary" style={{ fontSize: '0.82rem' }} disabled={busy} onClick={addLetter}>Add letter</button>
            </div>
          )}
          {err && <p style={{ color: colors.danger, fontSize: '0.85rem', margin: '0.5rem 0 0' }}>{err}</p>}
        </div>
      )}
    </div>
  );
}

function ManufacturerCodeRow({ row, canEdit, busy, onSave, onDelete }) {
  const [manufacturer, setManufacturer] = useState(row.manufacturer || '');
  const dirty = manufacturer !== (row.manufacturer || '');
  return (
    <tr style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
      <td style={{ ...tdStyle, fontWeight: 700, color: colors.textStrong }}>{row.letter}</td>
      <td style={tdStyle}>
        {canEdit
          ? <input value={manufacturer} onChange={e => setManufacturer(e.target.value)} style={{ ...inputStyle, padding: '0.3rem 0.5rem' }} />
          : (row.manufacturer || '—')}
      </td>
      {canEdit && (
        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {dirty && (
            <button className="ni-button ni-button--primary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', marginRight: 4 }} disabled={busy}
              onClick={() => onSave(row.letter, { manufacturer })}>Save</button>
          )}
          <button className="ni-button ni-button--ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} disabled={busy}
            onClick={() => onDelete(row.letter)}>×</button>
        </td>
      )}
    </tr>
  );
}

// Average metadata quality per catalogue family — a scorecard of which lines
// are best maintained.
function FamilyQualityPanel({ families, filterKey }) {
  const rows = (families || [])
    .filter(f => f.quality && (!filterKey || f.familyKey === filterKey))
    .sort((a, b) => b.quality.completeness - a.quality.completeness);
  if (rows.length === 0) return null;
  const bar = (val) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'flex-end' }}>
      <div style={{ width: 70, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.08)' }}>
        <div style={{ width: `${val}%`, height: '100%', borderRadius: 3, background: qualityColor(val) }} />
      </div>
      <span style={{ color: qualityColor(val), fontWeight: 700, minWidth: 30, textAlign: 'right' }}>{val}%</span>
    </div>
  );
  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem', overflowX: 'auto' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
        Metadata quality by family
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: colors.textMuted }}>
            <th style={thStyle}>Family</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Products</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Completeness</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Correctness</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Grade</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(f => (
            <tr key={f.familyKey} style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
              <td style={tdStyle}>{f.profile?.displayName || f.familyKey}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{f.productCount}</td>
              <td style={tdStyle}>{bar(f.quality.completeness)}</td>
              <td style={tdStyle}>{bar(f.quality.correctness)}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>
                <span style={{ fontWeight: 800, color: qualityColor(f.quality.completeness), border: `1px solid ${qualityColor(f.quality.completeness)}55`, borderRadius: 5, padding: '0 0.35rem' }}>{f.quality.grade}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FamilyAnalyticsPanel({ analytics, filterKey }) {
  const rows = (analytics || []).filter(a => !filterKey || a.familyKey === filterKey);
  if (rows.length === 0) return null;
  const fmtRate = r => (r == null ? '—' : `${(r * 100).toFixed(2)}%`);
  const fmtMtbf = (h, n) => (h == null ? '—' : `${h.toLocaleString()} h (${n})`);
  return (
    <div className="ni-panel" style={{ marginBottom: '1.25rem', overflowX: 'auto' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
        Family analytics — live device data
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: colors.textMuted }}>
            <th style={thStyle}>Family</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Production volume</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>RMAs</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>RMA rate</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>MTBF (samples)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.familyKey} style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
              <td style={tdStyle}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 6, background: r.accentColor || colors.accent }} />
                {r.displayName || r.familyKey}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{r.volume.toLocaleString()}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{r.rmaCount.toLocaleString()}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtRate(r.rmaRate)}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMtbf(r.mtbfHours, r.mtbfSamples)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Field = ({ label, value }) => value ? (
  <div style={fieldWrap}><span style={labelStyle}>{label}</span><span style={{ fontSize: '0.9rem', color: colors.textBody }}>{value}</span></div>
) : null;

const Input = ({ label, value, onChange, type = 'text' }) => (
  <label style={fieldWrap}><span style={labelStyle}>{label}</span>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />
  </label>
);

const cardStyle = {
  backgroundColor: colors.surface,
  border: `1px solid ${colors.panelBorder}`,
  borderRadius: '12px',
  padding: '0.85rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
  height: '100%',
  boxShadow: colors.shadow,
};
const labelStyle = { fontSize: '0.72rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const thStyle = { padding: '0.35rem 0.5rem', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' };
const tdStyle = { padding: '0.45rem 0.5rem', color: colors.textBody };
const toolbarBtn = (active) => ({
  fontSize: '0.82rem', whiteSpace: 'nowrap',
  borderColor: active ? colors.accent : undefined,
  color: active ? colors.accent : undefined,
});
const chipToggle = (on) => ({
  fontSize: '0.78rem', fontWeight: 600, padding: '0.3rem 0.7rem', borderRadius: '999px', cursor: 'pointer',
  border: `1px solid ${on ? colors.accent : colors.panelBorder}`,
  background: on ? 'rgba(70,187,137,0.16)' : 'transparent',
  color: on ? colors.accent : colors.textMuted,
});
const statusBadgeStyle = (color) => ({
  fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
  padding: '0.08rem 0.35rem', borderRadius: '5px', color,
  background: `${color}22`, border: `1px solid ${color}55`, whiteSpace: 'nowrap',
});
// Customer codes get the green accent tag; the house brand (00 = North Invent)
// gets a neutral tag so customer-specific products still stand out.
const companyTagStyle = (code) => {
  const house = code === '00';
  return {
    display: 'inline-block', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.03em',
    padding: '0.1rem 0.4rem', borderRadius: '6px',
    color: house ? colors.textMuted : colors.accent,
    background: house ? 'rgba(255,255,255,0.06)' : 'rgba(70,187,137,0.12)',
    border: `1px solid ${house ? colors.panelBorder : 'rgba(70,187,137,0.3)'}`,
  };
};
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' };
const fieldWrap = { display: 'flex', flexDirection: 'column', gap: '0.2rem' };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.6rem', fontSize: '0.88rem',
  borderRadius: '8px', border: `1px solid ${colors.panelBorder}`,
  background: 'rgba(255,255,255,0.04)', color: colors.textStrong,
};
